import lottie from 'lottie-web';
import { SplitText } from 'gsap/all';

import { Scroll } from '../_app/cuchillo/scroll/Scroll';
import { GetBy } from '../_app/cuchillo/core/Element';
import Scroll_Item from '../_app/cuchillo/scroll/Scroll_Item';
import { Maths } from '../_app/cuchillo/utils/Maths';

const RANGES = { lottie: [0.3, 0.8], show: [0.2, 0.3] };
const STEP_1 = .25;
const STEP_2 = .3;

class ScrollItem__StepPercentage extends Scroll_Item {

    constructor(__link, __index, __scroller) {
        super(__link, __index, __scroller);

        this._setupLottie(__link);
        this._setupText(__link);

        this.onMove = () => {
            const p = Maths.clamp(Maths.normalize(RANGES.lottie[1], RANGES.lottie[0], this.progressInside), 0, 1);
            this._drawLottie(p);

            const showP = Maths.clamp(Maths.normalize(RANGES.show[1], RANGES.show[0], this.progressInside), 0, 1);
            this._drawText(showP);
        };
    }

    _setupText(__link) {
        this._units = [];
        this.totalItems = 0;
        this.inc = 0;

        const container = GetBy.class("__textShow", __link)[0];
        if (!container) return;

        const words = new SplitText(container, {
            type: 'words',
            wordsClass: 'word-child'
        }).words;
        const wordUnits = words.map(el => ({
            type: 'word',
            el
        }));

        this._units = wordUnits.sort((a, b) =>
            (a.el.compareDocumentPosition(b.el) & Node.DOCUMENT_POSITION_FOLLOWING)
                ? -1
                : 1
        );

        this.totalItems = this._units.length;
        this.inc = this.totalItems > 0 ? 1 / this.totalItems : 0;

        this._drawText(0);
    }

    // Revela les paraules amb stagger: el rang [0,1] es reparteix en N finestres iguals (inc = 1/N).
    _drawText(showP) {
        let p0 = 0;
        let p1 = this.inc;

        for (let i = 0; i < this.totalItems; i++) {
            const vis = Maths.clamp(Maths.normalize(p1, p0, showP), 0, 1);
            this._units[i].el.style.opacity = vis;
            p0 = p1;
            p1 += this.inc;
        }
    }

    // Un "item" per cada `.lottie-container`: el seu `.lottie-label`, el `.lottie` i el seu Lottie.
    // El JSON de l'animació ve del contingut inline del `.lottie` (`{{ item.lottie|safe }}`) o, si no,
    // d'un `data-src` (URL a un .json). El SVG s'estira al 100% d'amplada via CSS + preserveAspectRatio.
    _setupLottie(__link) {
        this._items = [];

        const containers = [...GetBy.class('lottie-container', __link)];
        for (const container of containers) {
            const label = GetBy.class('lottie-label', container)[0] || null;
            const holder = GetBy.class('lottie', container)[0] || null;

            const item = { label, holder, anim: null };
            this._items.push(item);

            if (holder) {
                const data = this._readData(holder);
                if (data) {
                    holder.innerHTML = '';
                    item.anim = this._createAnim(holder, data);
                }
            }
        }

        // this._drawLottie(0);
    }

    // Crea el Lottie (renderer SVG, sense autoplay/loop) a partir del JSON i el deixa al frame 0.
    _createAnim(holder, data) {
        const anim = lottie.loadAnimation({
            container: holder,
            renderer: 'svg',
            loop: false,
            autoplay: false,
            animationData: data,
            rendererSettings: {
                // El SVG omple l'amplada del container mantenint la relació d'aspecte.
                preserveAspectRatio: 'xMidYMid meet',
                className: 'lottie-svg'
            }
        });
        anim.goToAndStop(0, true);
        return anim;
    }

    // Llegeix el JSON del Lottie: primer inline (textContent del `.lottie`), després `data-src`.
    // Retorna el JSON inline si n'hi ha; si és per URL, el carrega async i omple `item.anim`.
    _readData(holder) {
        const raw = holder.textContent.trim();
        if (raw) {
            try { return JSON.parse(raw); } catch (e) { console.error(e); }
        }
        // Fallback: carrega per URL de forma asíncrona i associa el Lottie al seu item.
        const src = holder.getAttribute('data-src');
        if (src) {
            const item = this._items.find(it => it.holder === holder);
            fetch(src)
                .then(r => r.json())
                .then(json => {
                    holder.innerHTML = '';
                    if (item) item.anim = this._createAnim(holder, json);
                })
                .catch(() => { });
        }
        return null;
    }

    // Aplica el progrés [0,1] als items amb STAGGER (com StepItems): el rang global es reparteix en N
    // finestres iguals (inc = 1/N), una per contenidor. Dins la finestra de cada item, el progrés local
    // es divideix en 3 passos: fade in del label, fade in del lottie i animació del Lottie.
    _drawLottie(p) {
        const n = this._items.length;
        if (!n) return;
        const inc = 1 / n;

        let p0 = 0;
        let p1 = inc;
        for (let i = 0; i < n; i++) {
            const item = this._items[i];
            const local = Maths.clamp(Maths.normalize(p1, p0, p), 0, 1);

            // Pas 1: fade in del label. Pas 2: fade in del lottie. Pas 3: animació del Lottie.
            const labelOpacity = Maths.clamp(Maths.normalize(STEP_1, 0, local), 0, 1);
            const lottieOpacity = Maths.clamp(Maths.normalize(STEP_2, STEP_1, local), 0, 1);
            const lottieProgress = Maths.clamp(Maths.normalize(1, STEP_2, local), 0, 1);

            if (item.label) item.label.style.opacity = labelOpacity;
            if (item.holder) item.holder.style.opacity = lottieOpacity;
            if (item.anim) {
                const total = item.anim.totalFrames || 0;
                item.anim.goToAndStop(lottieProgress * total, true);
            }

            p0 = p1;
            p1 += inc;
        }
    }

    dispose() {
        if (this._items) {
            for (let i = 0; i < this._items.length; i++) {
                if (this._items[i].anim) this._items[i].anim.destroy();
            }
            this._items = [];
        }
        super.dispose();
    }
}

Scroll._registerClass('StepPercentage', ScrollItem__StepPercentage);

export default ScrollItem__StepPercentage;
